'use client';

import { useCallback, useEffect, useMemo } from 'react';
import config from 'config';
import {
  useAddFavoriteLocationsMutation,
  useDeleteFavoriteLocationsMutation,
  useGetFavoriteLocationsQuery,
  useGetLocationsQuery,
  useGetStatesQuery
} from 'services/locationsApi';

import { Box } from '@chakra-ui/react';

import { useAppSelector } from 'store/hooks';

import { getAbbrState, groupLocationsByState } from 'utils';

import { ContentFilter, LocationInterface } from 'types';

import { ContentLoader } from 'components/ContentLoader';
import { SeeMore } from 'components/SeeMore';
import { LocationBlock } from '../LocationBlock';
import { LocationDropdown } from '../LocationDropdown';
import { LocationTiles } from '../LocationTiles';

const { isDropdownList } = config.locations;

interface LocationContainerProps {
  handleSelectLocation: (item: LocationInterface) => void;
  onNearMeHandler: () => void;
  onFavotiresHandler: () => void;
}

const LocationContainer = ({
  handleSelectLocation,
  onNearMeHandler,
  onFavotiresHandler
}: LocationContainerProps) => {
  const { userInfo } = useAppSelector((state) => state.persistAuth);
  const { user } = useAppSelector((state) => state.user);
  const { data: locations = [], isLoading: isLocationsLoading } =
    useGetLocationsQuery(ContentFilter.ServicesOnly);
  const { data: favoriteLocations, isLoading: isFavLoading } =
    useGetFavoriteLocationsQuery('', {
      skip: !userInfo
    }) || {};
  const [addFavLocation] = useAddFavoriteLocationsMutation() || {};
  const [deleteFavLocation] = useDeleteFavoriteLocationsMutation() || {};
  const { data: states = [], isLoading: isStatesLoading } =
    useGetStatesQuery('') ?? {};

  const isLogged = Boolean(userInfo?.clid) && Boolean(userInfo?.token);
  const homeLocationId = user?.home_store_id || '';
  const isLoading = isStatesLoading || isLocationsLoading || isFavLoading;
  const groupedLocations = groupLocationsByState(locations);
  const groupedStates = getAbbrState(states);
  const homeLocations = locations?.filter(
    (store) => homeLocationId === store.id
  );
  const firstItem = homeLocations[0];
  const stateName = groupedStates[firstItem?.province];

  const favoriteLocationIds = useMemo(
    () =>
      Array.isArray(favoriteLocations)
        ? favoriteLocations.map((loc) => loc.item_num)
        : [],
    [favoriteLocations]
  );

  const isLocationsExist = locations?.length > 0;
  const isHomeLocationsExist = homeLocations?.length > 0;

  const sortedLocationsArray = useMemo(
    () =>
      Object.entries(groupedLocations ?? {}).sort(([stateA], [stateB]) =>
        stateA.localeCompare(stateB)
      ),
    [groupedLocations]
  );

  useEffect(() => {
    if (locations.length < 2) {
      handleSelectLocation(locations[0]);
    }
  }, [locations, handleSelectLocation]);

  const onLocationClick = () => {
    handleSelectLocation(firstItem);
  };

  const onFavoriteIconClick = useCallback(
    (isSelected: boolean, locationId: number) => {
      if (isSelected) {
        deleteFavLocation(locationId);
      } else {
        addFavLocation(locationId);
      }
    },
    [deleteFavLocation, addFavLocation]
  );

  return (
    <Box position='relative' w='100%'>
      {locations.length > 5 ? (
        <LocationTiles
          onLocation={onLocationClick}
          onFavorites={onFavotiresHandler}
          onNearMe={onNearMeHandler}
          isLogged={isLogged}
          isHomeLocationsExist={isHomeLocationsExist}
          mb={{ base: '15px', md: '30px' }}
        />
      ) : (
        <SeeMore />
      )}

      {isLoading || !isLocationsExist ? (
        <ContentLoader isLoading={isLoading} label='No locations found' />
      ) : (
        <>
          {isHomeLocationsExist && locations.length < 6 && (
            <LocationBlock
              locations={homeLocations}
              state={stateName}
              label='YOUR LOCATION'
              onSelectLocation={handleSelectLocation}
              onFavoriteIconClick={onFavoriteIconClick}
              isMain
            />
          )}
          {locations.length > 5 && isDropdownList ? (
            <LocationDropdown
              locations={sortedLocationsArray}
              groupedStates={groupedStates}
              favoriteLocationIds={favoriteLocationIds}
              onSelectLocation={handleSelectLocation}
              onFavoriteIconClick={onFavoriteIconClick}
              isLogged={isLogged}
            />
          ) : (
            sortedLocationsArray.map(([state, locationItems]) => {
              const isValidLocations =
                state && locationItems.every((item) => item.name && item.city);
              return isValidLocations ? (
                <LocationBlock
                  key={state}
                  isHidedHeader={sortedLocationsArray.length === 1}
                  locations={locationItems}
                  state={groupedStates[state]}
                  onSelectLocation={handleSelectLocation}
                  onFavoriteIconClick={onFavoriteIconClick}
                  favoriteLocationIds={favoriteLocationIds}
                  isLogged={isLogged}
                />
              ) : null;
            })
          )}
        </>
      )}
    </Box>
  );
};

export default LocationContainer;
